import { Box, Typography } from "@mui/material";
import { INFINITE_SCROLL_DEFAULT_BATCH_SIZE, InfiniteScroll } from "@smartrr/shared/components/InfiniteScroll";
import { ItemSkeleton } from "@smartrr/shared/components/InfiniteScroll/components/ItemSkeleton";
import { optionalClassName, prefixClassName } from "@smartrr/shared/components/primitives";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { extractShopifyId, shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { getPurchasableFromPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import React, { useEffect, useMemo, useState } from "react";

import { handleValidatingPurchasables } from "@customer-app/account/config";
import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { useMobileQuery } from "@customer-app/account/views/OperationAquarius/utils";
import { useCustomerPortalVariantToPurchasableMap } from "@customer-app/state/reducers/purchasables";

import { ModalProductItemWrapperProps, ModalProductListProps } from "./libs/types";
import { useUnavailableVariantsAndDeliveries } from "../../../../../hooks/useUnavailableVariantsAndDeliveries";
import { VerticalProductWrapper } from "../../../constants";
import { Item } from "../../../Item";
import { useMissingVariantChecker } from "../../../utils";
import { sortAndFilterLineItemsForDisplay } from "../../../V2SubscriptionManagement/libs/utils";
import { BundleItem } from "../Bundles/BundleItem";
import { isCPSLineItemBundleParent } from "@smartrr/shared/utils/bundles";
import { useBasicSubscriptionInfo } from "../../ManageSubscriptionsModal/hooks/useBasicSubscriptionInfo";
import { useVariantEnablementForSellingPlanId } from "@customer-app/state/reducers/sellingPlanGroups";

export const ModalProductList = ({
  delivery,
  title,
  variantIdFromDropdown,
  initialLineItemId,
  showProductName,
  activePurchaseable,
  setActivePurchaseable,
  activeItem,
  setActiveItem,
  activeQty,
  onRemove,
  returnToModal,
  showVariant = true,
  hideAddons = false,
  showUnavailableTag = false,
  selectOnClick = true,
  isLoading = false,
  showQtySelector = true,
  disableWrap = false,
  showPrice = true,
  disabled = false,
  hideBundleItems = false,
  isPrepaid,
  isSellingPlanDeleted,
}: ModalProductListProps): JSX.Element => {
  const { skdIdx, purchaseState } = useBasicSubscriptionInfo(delivery);
  const [itemsToShow, setItemsToShow] = useState<IPurchaseStateLineItem[]>([]);
  const theme = useSmartrrTheme();
  const isMobile = useMobileQuery();

  const isViewHorizontal = theme.modernThemeSettings?.productScrollType === "horizontal" || disableWrap;

  const sortedAndFilteredLineItems = useMemo(
    () => sortAndFilterLineItemsForDisplay(purchaseState, skdIdx, hideAddons, hideBundleItems),
    [purchaseState]
  );

  /** Used in cases where we need to only display a specific line item that is to be swapped*/
  const singledOutLineItem = useMemo(
    () => sortedAndFilteredLineItems.find(lineItem => lineItem.id === initialLineItemId) ?? undefined,
    [sortedAndFilteredLineItems, initialLineItemId]
  );

  useEffect(() => {
    setItemsToShow(
      singledOutLineItem
        ? [singledOutLineItem]
        : sortedAndFilteredLineItems.slice(0, INFINITE_SCROLL_DEFAULT_BATCH_SIZE)
    );
  }, [purchaseState, singledOutLineItem]);

  const productListHeight = useMemo(() => {
    if (!isViewHorizontal && sortedAndFilteredLineItems.length > (isMobile ? 3 : 5)) {
      return "300px";
    }
    return "max-content";
  }, [isMobile, sortedAndFilteredLineItems.length]);

  if (!delivery) {
    return <React.Fragment />;
  }

  return (
    <React.Fragment>
      {title ? <Typography variant="h4">{title}</Typography> : null}
      {itemsToShow.length > 0 ? (
        <Box
          data-testid="modal-product-list-wrapper"
          sx={{
            overflowX: "scroll",
            height: "max-content",
            overflowY: "hidden",
            margin: "15px -20px 15px 0px",
          }}
          className={prefixClassName("modal-product-list-wrapper")}
        >
          <InfiniteScroll
            wrap={!isViewHorizontal}
            isParentLoading={isLoading}
            hasMore={sortedAndFilteredLineItems.length > itemsToShow.length}
            height={productListHeight}
            skeleton={<ItemSkeleton radius={theme.modernImageBorderRadius} />}
            horizontal
            handleAddItemsToScroll={numberOfItemsToLoad =>
              setItemsToShow(prevItems => [
                ...prevItems,
                ...sortedAndFilteredLineItems.slice(itemsToShow.length, numberOfItemsToLoad),
              ])
            }
          >
            {itemsToShow.map(item => (
              <ModalItemWrapper
                key={item.id}
                item={item}
                delivery={delivery}
                variantIdFromDropdown={variantIdFromDropdown}
                returnToModal={returnToModal}
                disabled={disabled}
                isLoading={isLoading}
                selectOnClick={selectOnClick}
                showPrice={showPrice}
                showQtySelector={showQtySelector}
                showUnavailableTag={showUnavailableTag}
                activeItem={activeItem}
                setActiveItem={setActiveItem}
                activePurchaseable={activePurchaseable}
                setActivePurchaseable={setActivePurchaseable}
                activeQty={activeQty}
                onRemove={onRemove}
                showProductName={showProductName}
                showVariant={showVariant}
                isPrepaid={isPrepaid}
                isSellingPlanDeleted={isSellingPlanDeleted}
              />
            ))}
          </InfiniteScroll>
        </Box>
      ) : null}
    </React.Fragment>
  );
};

const ModalItemWrapper = ({
  item: itemToRender,
  delivery,
  variantIdFromDropdown,
  activeItem,
  returnToModal,
  selectOnClick,
  showPrice,
  showQtySelector,
  showUnavailableTag,
  activePurchaseable,
  activeQty,
  setActivePurchaseable,
  onRemove,
  setActiveItem,
  isSellingPlanDeleted,
  showProductName = true,
  showVariant = true,
  isLoading = false,
  disabled = false,
  isPrepaid = false,
}: ModalProductItemWrapperProps) => {
  const { isSkipped, purchaseState, sellingPlanId, bundle } = useBasicSubscriptionInfo(delivery);
  const isVariantMissing = useMissingVariantChecker();
  const { unavailableVariantsAndDeliveries } = useUnavailableVariantsAndDeliveries();

  const variantToPurchasableMap = useCustomerPortalVariantToPurchasableMap();
  const { getMaxQuantity } = handleValidatingPurchasables(purchaseState, [], variantToPurchasableMap);
  const purchasable = getPurchasableFromPurchasableMap(variantToPurchasableMap, itemToRender.vnt);

  const enabledVariantOptions = useVariantEnablementForSellingPlanId(
    purchasable ?? undefined,
    sellingPlanId,
    false
  );

  if (!purchasable) {
    return <React.Fragment key={itemToRender.id} />;
  }

  const fullShopifyId = purchasable?.shopifyId ?? "";

  const variantFromDropdown = purchasable.vnts?.find(vnt => vnt.id === variantIdFromDropdown ?? "");

  // TODO: Move to new utils, useIsProductUnavailable & useIsVariantUnavailable
  const isUnavailable =
    unavailableVariantsAndDeliveries.vnts.some(vnt => vnt.id === itemToRender.vnt.id) ||
    unavailableVariantsAndDeliveries.purchasables.some(p => p.id === purchasable.id);

  // onRemove is only available for addons and sub items IF there are more than 1 sub item
  const hasMoreThanOneRecurringItem =
    purchaseState.stLineItems.filter(item => !item.isAddOn && !item.isRedeemed).length > 1;

  const isOnRemoveAvailable = itemToRender.isAddOn || hasMoreThanOneRecurringItem;

  const isSelected = itemToRender?.id === activeItem?.id;
  const isSelectable = !isPrepaid && !itemToRender.isRedeemed && selectOnClick;
  const isItemDisabled = isSkipped || isSellingPlanDeleted || disabled || isUnavailable || isLoading;

  const maxQuantity = getMaxQuantity(fullShopifyId);

  const isVntMissing = isVariantMissing(
    String(shopifyGidToNumber(itemToRender.vnt.shopifyId!)),
    purchaseState.sellingPlanId ?? ""
  );

  const isBundleParent = isCPSLineItemBundleParent(itemToRender);

  // Add-ons/Redeemed may also not be in the main subscription programs.
  const disableShowQuantity = !isVntMissing || itemToRender.isAddOn;

  const className = optionalClassName({
    "list-item": true,
    "list-item-bundle": isBundleParent,
    "list-item-disabled": isItemDisabled,
    "list-item-prepaid": isPrepaid,
    "list-item-selected": isSelected,
    "list-item-unavailable": isUnavailable,
  });

  return (
    <VerticalProductWrapper
      item
      key={itemToRender?.id!}
      className={className}
      data-smartrr-shopify-product-id={String(extractShopifyId(purchasable.shopifyId))}
    >
      {bundle && isBundleParent ? (
        <BundleItem
          delivery={delivery}
          item={itemToRender}
          product={purchasable!}
          returnToModal={returnToModal!}
          disabled={isItemDisabled}
          enabledVariantOptions={enabledVariantOptions}
        />
      ) : (
        <Item
          delivery={delivery}
          item={itemToRender}
          setItem={setActiveItem}
          activeItem={activeItem}
          product={purchasable!}
          setActivePurchaseable={setActivePurchaseable}
          activePurchaseable={activePurchaseable}
          activeQty={activeQty}
          disabled={isItemDisabled}
          selectOnClick={isSelectable}
          showProductName={showProductName}
          showVariant={showVariant}
          showQtySelector={showQtySelector ? disableShowQuantity : undefined}
          showPrice={showPrice}
          currentQty={itemToRender.quantity}
          isLoading={isLoading}
          // Only functionality is removing item
          isPrepaid={isPrepaid}
          onRemove={isOnRemoveAvailable ? onRemove : undefined}
          variantFromDropdown={variantFromDropdown}
          maxQty={maxQuantity === -1 ? undefined : maxQuantity}
          showUnavailableTag={showUnavailableTag}
        />
      )}
    </VerticalProductWrapper>
  );
};
